import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTier } from './useTier';
import { isIOS, isStandalonePWA } from '../utils/platform';
import {
  isCapacitorNative,
  ensureNativeSpeechPermission,
  startNativeSpeechRecognition,
  stopNativeSpeechRecognition,
} from '../utils/capacitor';
import { API_BASE_URL } from '../services/api';

interface PhotoResult {
  success: boolean;
  response?: string;
  loggedFood?: boolean;
  foods?: any[];
  meal?: any;
  mealType?: string;
  date?: string;
  photoUrl?: string;
  photoPublicId?: string;
  error?: string;
}

export const useAIInput = () => {
  const { user, updateUser } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const recognitionRef = useRef<any>(null);

  // useTier().isPro includes the Free Pro Day window — raw tier checks here
  // broke the day-1 "unlimited scans" promise (backend uses isEffectivePro)
  const { isPro } = useTier();

  // Helper to get local date string
  const getLocalDateString = (date: Date): string => {
    return date.toLocaleDateString('en-CA'); // YYYY-MM-DD format
  };

  // Check if web speech recognition is available. Native Capacitor flow
  // doesn't use webkitSpeechRecognition — it uses Apple's SFSpeechRecognizer
  // via our custom NativeSpeechRecognition plugin, which works in WKWebView
  // where the Web Speech API doesn't.
  const hasSpeechRecognition = (): boolean => {
    if (isCapacitorNative()) return true; // Native plugin is the source of truth
    return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
  };

  // Native (Capacitor iOS/Android) voice flow. Kicked off async from the
  // synchronous startVoiceRecognition() so the existing callers don't need
  // to change. Permission check + speech recognition both happen here.
  const startNativeVoiceFlow = useCallback(
    async (
      onResult: (transcript: string) => void,
      onError?: (message: string) => void,
    ) => {
      setIsListening(true);
      try {
        const granted = await ensureNativeSpeechPermission();
        if (!granted) {
          setIsListening(false);
          onError?.('Microphone or Speech Recognition permission was denied. Go to Settings → FuelTron and enable both.');
          return;
        }
        const transcript = await startNativeSpeechRecognition();
        setIsListening(false);
        if (transcript && transcript.trim().length > 0) {
          // Match the web flow's small delay before auto-sending so UI updates settle.
          setTimeout(() => onResult(transcript), 300);
        }
      } catch (err: any) {
        setIsListening(false);
        const msg = err?.message?.includes?.('No speech')
          ? "I didn't catch that. Tap the mic and try again."
          : `Voice input failed: ${err?.message || 'unknown error'}`;
        onError?.(msg);
      }
    },
    [],
  );

  // Start voice recognition.
  //
  // Three modes (decided at runtime):
  //   1. Capacitor native (iOS/Android shell) → custom NativeSpeechRecognition plugin
  //      uses Apple's SFSpeechRecognizer; works in WKWebView where Web Speech doesn't.
  //   2. iOS Safari (and standard browsers) → webkitSpeechRecognition (Web Speech API).
  //      iOS standalone PWA has Web Speech disabled by Apple — caller can only
  //      reopen in Safari, so we surface that error.
  //   3. Other browsers without Web Speech support → return false with error.
  const startVoiceRecognition = useCallback((
    onResult: (transcript: string) => void,
    onError?: (message: string) => void,
  ): boolean => {
    if (isCapacitorNative()) {
      // Fire-and-forget native flow; setIsListening is managed inside.
      startNativeVoiceFlow(onResult, onError);
      return true;
    }

    if (!hasSpeechRecognition()) {
      const msg = isIOS()
        ? 'Voice input is not supported on this iOS browser. Please use Safari.'
        : 'Voice input is not supported in this browser. Try Chrome, Edge, or Safari.';
      onError?.(msg);
      return false;
    }

    if (isIOS() && isStandalonePWA()) {
      onError?.('Voice input is disabled when FuelTron is launched from the iOS home screen. Please open the app in Safari to use voice logging.');
      return false;
    }

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setIsListening(false);

      if (transcript && transcript.trim().length > 0) {
        // Small delay before auto-sending
        setTimeout(() => {
          onResult(transcript);
        }, 300);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event);
      setIsListening(false);
      const code = event?.error || 'unknown';
      let msg: string;
      switch (code) {
        case 'not-allowed':
        case 'service-not-allowed':
          msg = isIOS()
            ? 'Microphone access was denied. Open iOS Settings → Safari → Microphone and allow access for this site.'
            : 'Microphone access was denied. Allow microphone access in your browser settings and try again.';
          break;
        case 'no-speech':
          msg = "I didn't catch that. Tap the mic and try again.";
          break;
        case 'audio-capture':
          msg = 'No microphone was found on this device.';
          break;
        case 'network':
          msg = 'Voice recognition needs an internet connection. Check your connection and try again.';
          break;
        case 'aborted':
          // User stopped manually — no message needed
          return;
        default:
          msg = `Voice recognition failed (${code}). Please try again.`;
      }
      onError?.(msg);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    try {
      recognition.start();
      setIsListening(true);
      recognitionRef.current = recognition;
      return true;
    } catch (error: any) {
      console.error('Failed to start speech recognition:', error);
      setIsListening(false);
      onError?.(`Failed to start voice input: ${error?.message || 'unknown error'}`);
      return false;
    }
  }, []);

  // Stop voice recognition. Branches on platform — native plugin has its own
  // stop call that resolves the start() promise with null.
  const stopVoiceRecognition = useCallback(() => {
    if (isCapacitorNative()) {
      stopNativeSpeechRecognition().finally(() => setIsListening(false));
      return;
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, []);

  // Process photo file
  const processPhoto = useCallback(async (
    file: File,
    additionalMessage?: string
  ): Promise<PhotoResult> => {
    setIsProcessingPhoto(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      if (additionalMessage && additionalMessage.trim()) {
        formData.append('message', additionalMessage.trim());
      }

      // Add client date and time for timezone handling
      const clientDate = getLocalDateString(new Date());
      formData.append('clientDate', clientDate);
      formData.append('clientTime', new Date().getHours().toString());

      const response = await fetch(`${API_BASE_URL}/ai-assistant/photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to analyze photo');
      }

      const data = await response.json();

      // Update usage counters
      if (user && updateUser) {
        const updatedUser = {
          ...user,
          usage: {
            ...user.usage,
            aiAssistantMessages: user.usage.aiAssistantMessages + 1,
          }
        };
        await updateUser(updatedUser);
      }

      // Notify if food was logged
      if (data.loggedFood) {
        window.dispatchEvent(new CustomEvent('foodLogged', { detail: { date: data.date } }));
      }

      return {
        success: true,
        response: data.response,
        loggedFood: data.loggedFood,
        foods: data.foods,
        meal: data.meal,
        mealType: data.mealType,
        date: data.date,
        photoUrl: data.photoUrl,
        photoPublicId: data.photoPublicId,
      };
    } catch (error: any) {
      console.error('Photo analysis error:', error);
      return {
        success: false,
        error: error.message || 'Photo analysis failed. Please try again later.',
      };
    } finally {
      setIsProcessingPhoto(false);
    }
  }, [user, updateUser]);

  // Create object URL for preview
  const createPhotoPreview = (file: File): string => {
    return URL.createObjectURL(file);
  };

  // Revoke object URL to free memory
  const revokePhotoPreview = (url: string) => {
    URL.revokeObjectURL(url);
  };

  return {
    // Voice
    isListening,
    hasSpeechRecognition: hasSpeechRecognition(),
    startVoiceRecognition,
    stopVoiceRecognition,

    // Photo
    isProcessingPhoto,
    processPhoto,
    createPhotoPreview,
    revokePhotoPreview,

    // User state
    isPro,
  };
};
